import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import ConfiguracaoFotoViewModel, { ConfiguracaoFoto } from '../../viewmodel/ConfiguracaoFotoViewModel';

const ConfiguracaoFotoPage: React.FC = () => {
  const [configuracao, setConfiguracao] = useState<ConfiguracaoFoto | null>(null);
  const [tamanhoMinimoKb, setTamanhoMinimoKb] = useState<number>(300);
  const [tamanhoMaximoMb, setTamanhoMaximoMb] = useState<number>(10);
  const [formatosAceitos, setFormatosAceitos] = useState<string>('image/jpeg,image/png');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await ConfiguracaoFotoViewModel.buscar();
      setConfiguracao(data);
      if (data) {
        setTamanhoMinimoKb(data.tamanhoMinimoKb);
        setTamanhoMaximoMb(data.tamanhoMaximoMb);
        setFormatosAceitos(data.formatosAceitos);
      }
    } catch (error) {
      toast.error(BackendErrorMessage(error) || 'Erro ao carregar configurações de foto.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const handleSalvar = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!Number.isFinite(tamanhoMinimoKb) || tamanhoMinimoKb <= 0) {
      toast.error('Tamanho mínimo deve ser maior que zero.');
      return;
    }
    if (!Number.isFinite(tamanhoMaximoMb) || tamanhoMaximoMb <= 0) {
      toast.error('Tamanho máximo deve ser maior que zero.');
      return;
    }
    if (!formatosAceitos.trim()) {
      toast.error('Informe ao menos um formato aceito.');
      return;
    }

    setSaving(true);
    try {
      const salvo = await ConfiguracaoFotoViewModel.salvar({
        tamanhoMinimoKb,
        tamanhoMaximoMb,
        formatosAceitos: formatosAceitos.trim(),
      });
      setConfiguracao(salvo);
      toast.success('Configurações de foto atualizadas.');
    } catch (error) {
      toast.error(BackendErrorMessage(error) || 'Erro ao salvar configurações de foto.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Configurações de Foto</h2>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Form onSubmit={handleSalvar}>
          {!configuracao && (
            <Alert variant="warning" className="mb-4">
              Nenhuma configuração encontrada. Preencha os dados abaixo para criar.
            </Alert>
          )}

          <Row>
            <Col md={12} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-file-earmark-arrow-down fs-3 me-3 text-primary"></i>
                    <Card.Title className="mb-0">Tamanho Mínimo</Card.Title>
                  </div>
                  <Card.Text className="text-muted mb-3">
                    Tamanho mínimo aceito para upload de fotos de candidatos via integração.
                  </Card.Text>
                  <Form.Group>
                    <Form.Label>Kilobytes (KB)</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      value={tamanhoMinimoKb}
                      onChange={(e) => setTamanhoMinimoKb(Number(e.target.value))}
                    />
                    <Form.Text className="text-muted">
                      Ex: 100 KB
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col md={12} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-file-earmark-arrow-up fs-3 me-3 text-warning"></i>
                    <Card.Title className="mb-0">Tamanho Máximo</Card.Title>
                  </div>
                  <Card.Text className="text-muted mb-3">
                    Tamanho máximo aceito para upload de fotos de candidatos via integração.
                  </Card.Text>
                  <Form.Group>
                    <Form.Label>Megabytes (MB)</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      value={tamanhoMaximoMb}
                      onChange={(e) => setTamanhoMaximoMb(Number(e.target.value))}
                    />
                    <Form.Text className="text-muted">
                      Ex: 10 MB
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col md={12} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-file-image fs-3 me-3 text-success"></i>
                    <Card.Title className="mb-0">Formatos Aceitos</Card.Title>
                  </div>
                  <Card.Text className="text-muted mb-3">
                    Content-types aceitos, separados por vírgula.
                  </Card.Text>
                  <Form.Group>
                    <Form.Label>MIME types</Form.Label>
                    <Form.Control
                      type="text"
                      value={formatosAceitos}
                      onChange={(e) => setFormatosAceitos(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                      Ex: image/jpeg,image/png
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="d-grid">
            <Button type="submit" size="lg" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar configurações'}
            </Button>
          </div>
        </Form>
      )}
    </Container>
  );
};

export default ConfiguracaoFotoPage;
